import { computed } from "vue";
import type { ComputedRef, WritableComputedRef } from "vue";
import {
  createI18n as createVueI18n,
  useI18n as useVueI18n,
} from "vue-i18n";
import de from "@/locales/de.json";
import en from "@/locales/en.json";
import { getBranding, type BrandingName } from "@/lib/branding";

export type Locale = "de" | "en";
export type Translate = (key: string, ...args: unknown[]) => string;

export interface I18nContext {
  t: Translate;
  locale: WritableComputedRef<Locale>;
  availableLocales: readonly Locale[];
  setLocale(next: Locale): void;
  localeLabel: ComputedRef<string>;
}

const STORAGE_KEY = "token.portal.locale";
const AVAILABLE_LOCALES: readonly Locale[] = ["de", "en"];

function isLocale(value: string | null | undefined): value is Locale {
  return value === "de" || value === "en";
}

function detectLocale(): Locale {
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (isLocale(stored)) return stored;
  const browser = window.navigator.language.split("-")[0];
  return isLocale(browser) ? browser : "de";
}

interface Messages {
  [key: string]: string | Messages;
}

function mergeMessages<Base extends Messages, Brand extends Messages>(
  base: Base,
  brand: Brand,
): Base & Brand {
  const messages: Messages = { ...base };
  for (const [key, value] of Object.entries(brand)) {
    const existing = messages[key];
    messages[key] =
      typeof existing === "object" && typeof value === "object"
        ? mergeMessages(existing, value)
        : value;
  }
  return messages as Base & Brand;
}

export function getBrandMessages(branding: BrandingName) {
  const { messages } = getBranding(branding);
  return {
    de: mergeMessages(de, messages.de),
    en: mergeMessages(en, messages.en),
  };
}

export function createI18n(branding: BrandingName = "demo") {
  const locale = detectLocale();
  const messages = getBrandMessages(branding);
  document.documentElement.lang = locale;
  return createVueI18n<[typeof messages.de], Locale, false>({
    legacy: false,
    locale,
    fallbackLocale: "de",
    messages,
  });
}

export function useI18n(): I18nContext {
  const { t, locale } = useVueI18n();
  const currentLocale = locale as WritableComputedRef<Locale>;
  const translate = t as unknown as Translate;

  const setLocale = (next: Locale) => {
    currentLocale.value = next;
    window.localStorage.setItem(STORAGE_KEY, next);
    document.documentElement.lang = next;
  };

  const localeLabel = computed(() => translate(`app.locale.${currentLocale.value}`));

  return {
    t: translate,
    locale: currentLocale,
    availableLocales: AVAILABLE_LOCALES,
    setLocale,
    localeLabel,
  };
}
